import { apprMenuFor } from './appr-menu';
import { attMenuFor } from './att-menu';
import { APPR_LAYOUT, ATT_LAYOUT, regroup } from './menu-layout';
// 인사(2026-10-17 · web 레인 W7a) — 담당자 보기는 배치표로, 직원 보기는 오늘 트리 그대로(menu-layout 머리말)
import { hrMenuFor } from './hr-menu';
import { HR_ADMIN_LAYOUT } from './menu-layout';

/**
 * 쪽 머리의 경로 · 범위 칩 — 메뉴 배치에서 만든다 (web 레인 W6 · 계획 F4 · 평 2차 «경로 꼴이 여덟»).
 *
 * 경로는 «도메인 › 묶음 › 화면» 한 꼴이다(시안 shellC.head — 예: 근태 › 연차 › 보상휴가).
 * ★ 손으로 적지 않는다 — 2열 메뉴 배치(`menu-layout.ts`)에서 찾는다. 그래서 메뉴를 옮기면 경로도 따라온다.
 *   종전에는 화면마다 글자로 적어 «업무 › …» · «근태 › 근태 관리» · «/» 가 섞였다.
 * ★ 주소는 그 화면이 «제 주소»로 넘긴다(라우터 훅을 부르지 않는다) — 라우터 없이 그리는 시험에서도 산다.
 *
 * 범위 칩(시안 «관리자 화면» · «내 것»)은 담당자 묶음의 화면에 «관리자 화면», 담당자 화면과 이름이
 * 닮은 본인 화면(내 지출결의 ↔ 지출 집행 · 내 위임 ↔ 위임)에 «내 것» 을 단다(평 2차 «3. 권한»).
 */

interface Domain {
  root: string;
  name: string;
  groups: ReadonlyArray<{ label: string; items: ReadonlyArray<{ to?: string; label: string }> }>;
  adminGroups: ReadonlySet<string>;
  /** 직원 보기의 트리(인사 — `/erp/hr` 가 «내 인사 홈» 이다). 없으면 담당자 보기와 같다. */
  staffGroups?: Domain['groups'];
}

const DOMAINS: readonly Domain[] = [
  {
    root: '/approval',
    name: '전자결재',
    groups: regroup(apprMenuFor(true), APPR_LAYOUT),
    adminGroups: new Set(['결재 운영', '돈 · 예산']),
  },
  {
    root: '/erp/attendance',
    name: '근태',
    // 담당자 보기(교대 포함) + 팀장 보기(맡은 조직) — 경로는 모든 갈래의 화면을 알아야 한다
    groups: regroup([...attMenuFor(true, false, true, false), ...attMenuFor(false, true, false, false)], ATT_LAYOUT),
    adminGroups: new Set(['근태 관리', '연차', '회사 기준']),
  },
  {
    root: '/erp/hr',
    name: '인사',
    // 담당자 보기(맡은 조직 포함) — 시안 hr_tree2 의 경로(«인사 › 직원 › 인사발령»)
    groups: regroup(hrMenuFor(true, true, false), HR_ADMIN_LAYOUT),
    adminGroups: new Set(['현황', '직원', '인사기록', '조직 · 직급', '계정 · 기준']),
    staffGroups: hrMenuFor(false, true, false),
  },
];

/** 담당자 화면과 이름이 닮은 본인 화면 — «내 것» 칩 */
const MINE: ReadonlySet<string> = new Set([
  '/approval/expense', '/approval/me/delegates',
  // 인사 본인 화면(시안 59 · 60 scope=me) — 담당자의 직원 카드 · 증명서 확인 · 발급과 닮았다
  '/erp/hr/me/record', '/erp/hr/me/certificates',
]);

/** 경로를 만드는 덧말 — 메뉴 밖 화면의 제 이름(`leaf`) · 직원 보기(`view: 'staff'`). */
export interface CrumbOptions {
  /** 메뉴 항목 «아래» 화면(입사 처리 등)이나 메뉴에 없는 화면의 이름 — 경로 끝에 붙는다 */
  leaf?: string;
  /** 직원 보기의 트리로 찾는다(인사 홈 = «내 인사 홈») */
  view?: 'staff';
}

function locate(path: string, view?: 'staff'): { domain: Domain; group?: string; label?: string; to?: string; exact?: boolean } | undefined {
  const domain = DOMAINS.find((d) => path === d.root || path.startsWith(`${d.root}/`));
  if (!domain) return undefined;
  let best: { group: string; label: string; to: string; len: number } | undefined;
  const groups = view === 'staff' && domain.staffGroups ? domain.staffGroups : domain.groups;
  for (const g of groups) {
    for (const i of g.items) {
      if (!i.to) continue;
      if (path === i.to) return { domain, group: g.label, label: i.label, exact: true };
      const under = i.to !== domain.root && path.startsWith(`${i.to}/`);
      if (under && (!best || i.to.length > best.len)) best = { group: g.label, label: i.label, to: i.to, len: i.to.length };
    }
  }
  return best ? { domain, group: best.group, label: best.label, to: best.to } : { domain };
}

/** «도메인 › 묶음 › 화면». 메뉴에 없는 주소는 가장 가까운 윗 화면까지, 그것도 없으면 도메인만. */
export function crumbsFor(path: string, opts: CrumbOptions = {}): string[] {
  const at = locate(path, opts.view);
  if (!at) return [];
  const base = [at.domain.name, ...(at.group ? [at.group] : []), ...(at.label ? [at.label] : [])];
  // 메뉴 항목 아래 화면 · 메뉴 밖 화면은 제 이름을 끝에(시안 50 «인사 › 직원 › 직원 관리 › 입사 처리»)
  return opts.leaf && !at.exact ? [...base, opts.leaf] : base;
}

/** 범위 칩 — 담당자 묶음이면 'admin', 본인 화면이면 'me', 그 밖은 없음. */
export function scopeFor(path: string, opts: CrumbOptions = {}): 'admin' | 'me' | undefined {
  if (MINE.has(path)) return 'me';
  const at = locate(path, opts.view);
  return at?.group && at.domain.adminGroups.has(at.group) ? 'admin' : undefined;
}

/**
 * 경로 칸마다 갈 주소(`PageHeader breadcrumbLinks`) — 메뉴 항목 «아래» 화면(`leaf`)에서만
 * 그 항목 칸이 링크가 된다(옛 입사 처리 경로 «인사 / 직원 관리(링크) / 입사 처리» 와 같은 자리).
 * 그 밖의 칸 · 메뉴 화면 그 자체 · 메뉴 밖 화면은 링크가 없다 — 지금 보던 경로 그대로다. (web 레인 W7b)
 */
export function crumbLinksFor(path: string, opts: CrumbOptions = {}): Array<string | undefined> {
  const crumbs = crumbsFor(path, opts);
  const at = locate(path, opts.view);
  if (!at || at.exact || !opts.leaf || !at.to) return crumbs.map(() => undefined);
  return crumbs.map((_, i) => (i === crumbs.length - 2 ? at.to : undefined));
}
